import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { environment } from "src/environments/environment";
import { NotFoundComponent } from "./core/components";
import { adminRolesList } from "./core/constants/admin-roles";
import { isLocal } from "./core/constants/is-development";
import { EnvironmentId } from "./core/enums/environment-id.enum";
import { concatCanActivateFn } from "./core/functions/concat-guards";
import { anyRoleGuard, authGuard, termsGuard } from "./core/guards";
import { AppRoutes } from "./core/models/app-route.model";
import { devAccessGuard } from "./shared/guards/dev-access/dev-access.guard";

export const APP_ROUTES: AppRoutes = [
  // Maintain backwards compatibility with old routes (some of these urls are used outside myRegistration)
  {
    path: "login",
    redirectTo: "/startup",
    pathMatch: "prefix",
  },
  {
    path: "terms-and-conditions",
    redirectTo: "terms-of-use",
    pathMatch: "prefix",
  },
  {
    path: "session-closed",
    redirectTo: "startup",
    pathMatch: "full",
  },
  {
    path: "info",
    redirectTo: "startup",
    pathMatch: "full",
  },
  {
    path: "unauthorized",
    redirectTo: "error/unauthorized",
    pathMatch: "full",
  },
  {
    // Redirect ALL routes prepended by "app" to the app's root (ie: "app/login/*", etc.)
    path: "app",
    pathMatch: "prefix",
    children: [{ path: "**", children: [] }],
    canActivate: [(_, { url }) => inject(Router).parseUrl(url.replace("/app", ""))],
  },
  {
    path: "error",
    title: "WEB_PAGE_TITLE.ERROR",
    data: {
      noIndex: true,
    },
    loadChildren: () => import("./core/components/error-page/error-page.routes").then((r) => r.ERROR_PAGE_ROUTES),
  },
  {
    path: "",
    title: "WEB_PAGE_TITLE.MAIN",
    loadComponent: () => import("./core/layouts/main-layout/main-layout.component").then((c) => c.MainLayoutComponent),
    pathMatch: "prefix",
    canActivate: [devAccessGuard],
    data: {
      noIndex: environment.id !== EnvironmentId.Production && !isLocal,
    },
    // Routes that use the Main Layout
    loadChildren: (): AppRoutes => [
      {
        path: "startup",
        title: "WEB_PAGE_TITLE.STARTUP",
        loadChildren: () => import("./startup/startup.routes").then((r) => r.STARTUP_ROUTES),
      },
      {
        path: "terms-of-use",
        title: "WEB_PAGE_TITLE.TERMS_OF_USE",
        loadChildren: () => import("./terms-and-conditions/terms-and-conditions.routes").then((r) => r.TERMS_AND_CONDITIONS_ROUTES),
        data: {
          disablePreload: true,
        },
      },
      {
        path: "admin",
        title: "WEB_PAGE_TITLE.ADMIN",
        canActivate: [concatCanActivateFn(authGuard, termsGuard, anyRoleGuard(...adminRolesList))],
        data: {
          // only a few users have access, requires some admin role
          disablePreload: true,
          noIndex: true,
        },
        loadChildren: () => import("./backoffice/backoffice.routes").then((r) => r.BACKOFFICE_ROUTES),
      },
      {
        path: "",
        pathMatch: "prefix",
        loadChildren: () => import("./frontoffice/frontoffice.routes").then((r) => r.FRONTOFFICE_ROUTES),
      },
    ],
  },
  {
    path: "**",
    title: "WEB_PAGE_TITLE.NOT_FOUND",
    data: {
      noIndex: true,
    },
    component: NotFoundComponent,
  },
];
